import { type DependencyList, useEffect, useRef } from 'react';

import { usePico } from '../pico';

/**
 * Like useEffect, but defers execution until PicoX is initialized.
 *
 * PicoX initializes asynchronously (createPicoX awaits persistence setup),
 * so `usePico()` returns null on the first render. Any useEffect that calls
 * a tracking function during that window silently drops the event and never
 * retries, because the tracking function's identity only changes when pico
 * changes — which is not reflected in the effect's dependency array.
 *
 * usePicoEffect solves this by:
 *   1. Reading pico from context internally and adding it to the dep array,
 *      so the effect automatically re-runs when pico transitions null → ready.
 *   2. Using an effectRef so the callback always captures fresh closures,
 *      without requiring the caller to add tracking functions to deps.
 *   3. Skipping execution while pico is null, guaranteeing the callback only
 *      runs when tracking will succeed.
 *
 * ### `once` option
 * When `once: true`, the hook fires at most once per component lifetime.
 * The effect should return `false` to signal "not ready yet" — the hook will
 * retry on subsequent dep changes without consuming the once budget.
 * Any other return value (including void/undefined) marks the effect as done.
 *
 * @example — basic (re-fires when deps change)
 * usePicoEffect(() => {
 *   trackSomething({ query });
 * }, [query]);
 *
 * @example — once (fires once, retries if effect returns false)
 * usePicoEffect(() => {
 *   if (!data) return false; // not ready, try again when deps change
 *   trackPageDisplayed({ id: data.id });
 * }, [data], { once: true });
 */
export function usePicoEffect(
  effect: () => boolean | void,
  deps: DependencyList,
  options?: { once?: boolean },
): void {
  const pico = usePico();
  const effectRef = useRef(effect);
  effectRef.current = effect;

  const once = options?.once ?? false;
  const firedRef = useRef(false);

  useEffect(
    () => {
      if (pico === null) return;
      if (once && firedRef.current) return;
      const result = effectRef.current();
      if (once && result !== false) {
        firedRef.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pico, ...deps],
  );
}
